import { Spinner } from "@/components/loader/spinner";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { BLUR_DATA_URL } from "@/config/site";
import Autoplay from "embla-carousel-autoplay";
import Fade from "embla-carousel-fade";
import Image from "next/image";
import { useMemo, useState, type SyntheticEvent } from "react";
import {
  buildHotelImageUrl,
  HOTEL_FALLBACK_IMAGE,
} from "../../lib/hotel-image";
import { IHotelImages } from "../../types/hotel-search-page-type";

type Props = {
  images: IHotelImages;
};

const loadedImageUrlCache = new Set<string>();

const handleImageError = (event: SyntheticEvent<HTMLImageElement>) => {
  const target = event.currentTarget;

  if (target.src.endsWith(HOTEL_FALLBACK_IMAGE)) {
    return;
  }

  target.src = HOTEL_FALLBACK_IMAGE;
};

const HotelListImageCarousel = ({ images }: Props) => {
  const [loadedImages, setLoadedImages] = useState<Record<number, boolean>>({});
  const [hasShownInitialLoader, setHasShownInitialLoader] = useState(false);
  const imageItems = useMemo(
    () =>
      images?.additional_images?.length > 0
        ? images.additional_images
        : [
            {
              id: 0,
              image_url: images?.main_image ?? "",
              image_caption: "hotel_image",
            },
          ],
    [images?.additional_images, images?.main_image],
  );
  const resolvedImageItems = useMemo(
    () =>
      imageItems.map((item) => ({
        ...item,
        resolvedSrc: buildHotelImageUrl(item.image_url),
      })),
    [imageItems],
  );
  const firstImageSrc =
    resolvedImageItems[0]?.resolvedSrc ?? HOTEL_FALLBACK_IMAGE;
  const showInitialSpinner =
    !hasShownInitialLoader &&
    !loadedImageUrlCache.has(firstImageSrc) &&
    !loadedImages[0];

  return (
    <Carousel
      className="m-0 w-full p-0"
      opts={{ loop: true }}
      plugins={[
        Autoplay({
          delay: 4000,
        }),
        Fade(),
      ]}
    >
      <CarouselContent>
        {resolvedImageItems.map((item, index) => (
          <CarouselItem key={index} className="pl-0">
            <div className="relative overflow-hidden bg-muted/40">
              {index === 0 && showInitialSpinner ? (
                <div className="pointer-events-none absolute inset-0 z-10 flex h-45 w-full items-center justify-center bg-background/35 backdrop-blur-[1px]">
                  <Spinner size="md" className="text-primary" />
                </div>
              ) : null}

              <Image
                alt={item.image_caption || "hotel_image"}
                src={item.resolvedSrc}
                width={500}
                height={500}
                className={`h-45 w-full object-cover transition-[filter,opacity,transform] duration-700 ease-out ${
                  loadedImages[index]
                    ? "scale-100 opacity-100 blur-0"
                    : "scale-[1.03] opacity-0 blur-md"
                }`}
                sizes="(max-width: 768px) 100vw, 240px"
                loading={index === 0 ? "eager" : "lazy"}
                priority={index === 0}
                fetchPriority={index === 0 ? "high" : "auto"}
                quality={70}
                unoptimized
                placeholder="blur"
                blurDataURL={BLUR_DATA_URL}
                onLoad={() => {
                  loadedImageUrlCache.add(item.resolvedSrc);

                  setLoadedImages((current) =>
                    current[index] ? current : { ...current, [index]: true },
                  );

                  if (index === 0) {
                    setHasShownInitialLoader(true);
                  }
                }}
                onError={handleImageError}
              />
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
    </Carousel>
  );
};

export default HotelListImageCarousel;
